import { Stack } from "@mui/material";
import {
  AddedItem,
  CategoryTitle,
  DiffGroupWrapper,
  DiffRow,
  DiffValue,
  RemovedItem,
} from "components/search/input/DifferencePanel/common";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import {
  DiffFixedMixOverride,
  FixedMixOverride,
  MaterialQuantity,
  MixCreate,
} from "store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";

interface Props {
  diff: DiffFixedMixOverride;
  base: FixedMixOverride | null;
}

export const FixedMixOverrideDiffItem = ({ diff, base }: Props) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const formatNumber = useFormatNumber();
  const formatQuantityValue = formatNumber(2);

  const fallback = t("noFixedMix");

  const formatQuantity = (quantity: MaterialQuantity): string => {
    const value = formatQuantityValue(quantity.value);
    switch (quantity.unit) {
      case "mass":
        return `${value}${units("mass")}`;
      case "grab":
        return `${value} ${t("grabs")}`;
      case "ratio":
        return value;
    }
  };

  const formatMix = (mix: MixCreate | null | undefined): string => {
    if (mix == null || mix.baskets.length === 0) return fallback;
    return mix.baskets
      .map((basket) => {
        const layerSummary = basket.layers
          .map(
            (layer) =>
              `${layer.material_name} ${formatQuantity(layer.quantity)}`
          )
          .join(", ");
        return `#${basket.basket_number}: ${layerSummary}`;
      })
      .join(" | ");
  };

  const { previous, updated, key } = diff;
  const isAdded = previous === null;
  const isRemoved = updated === null;

  const effectivePrevious = isAdded ? base : previous;
  const effectiveUpdated = isRemoved ? base : updated;

  return (
    <DiffGroupWrapper>
      {isAdded ? (
        <AddedItem>{key}</AddedItem>
      ) : isRemoved ? (
        <RemovedItem>{key}</RemovedItem>
      ) : (
        <CategoryTitle>{key}</CategoryTitle>
      )}
      <Stack sx={{ pl: 2 }}>
        <DiffRow
          title={t("mix")}
          value={
            <DiffValue>
              {formatMix(effectivePrevious?.mix)} →{" "}
              {formatMix(effectiveUpdated?.mix)}
            </DiffValue>
          }
        />
      </Stack>
    </DiffGroupWrapper>
  );
};
